'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getSocketConfig } from '@/actions/socket';
import { useSession } from 'next-auth/react';
import { logger } from '@/lib/client-logger';

interface ClientToServerEvents {
  join: (data: { room: string }) => void;
  leave: (data: { room: string }) => void;
}

interface SocketContextType {
  socket: Socket<any, ClientToServerEvents> | null;
  isConnected: boolean;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinRoom: () => {},
  leaveRoom: () => {},
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket<
    any,
    ClientToServerEvents
  > | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { data: session } = useSession();

  useEffect(() => {
    let socketInstance: Socket<any, ClientToServerEvents> | null = null;

    if (!session?.strapiToken) {
      return;
    }

    const initSocket = async () => {
      try {
        const config = await getSocketConfig();

        if (!config.url) {
          logger.error(
            'WebSocket URL is missing; socket connection will not be established',
          );
          return;
        }

        socketInstance = io(config.url, {
          auth: { token: session.strapiToken },
          withCredentials: false,
          transports: ['polling', 'websocket'],
          autoConnect: true,
        });

        socketInstance.on('connect', () => {
          logger.debug('Socket connected', { socketId: socketInstance?.id });
          setIsConnected(true);
        });

        socketInstance.on('disconnect', (reason) => {
          logger.debug('Socket disconnected', { reason });
          setIsConnected(false);
        });

        socketInstance.on('connect_error', (error) => {
          logger.error('Socket connection error', error);
          setIsConnected(false);
        });

        socketInstance.on('authenticated', (data: { username: string }) => {
          logger.debug('Socket authenticated', { username: data.username });
        });

        socketInstance.on('error', (data: { message: string }) => {
          logger.error('Socket error', undefined, { message: data.message });
        });

        setSocket(socketInstance);
      } catch (error) {
        logger.error('Failed to initialize socket', error);
      }
    };

    void initSocket();

    return () => {
      if (socketInstance) {
        logger.debug('Disconnecting socket');
        socketInstance.disconnect();
      }
    };
  }, [session]);

  const joinRoom = (room: string) => {
    if (socket?.connected) {
      socket.emit('join', { room });
      logger.debug('Joining socket room', { room });
    }
  };

  const leaveRoom = (room: string) => {
    if (socket?.connected) {
      socket.emit('leave', { room });
      logger.debug('Leaving socket room', { room });
    }
  };

  return (
    <SocketContext.Provider
      value={{ socket, isConnected, joinRoom, leaveRoom }}
    >
      {children}
    </SocketContext.Provider>
  );
};
